import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE } from '../../lib/config';
import {
  PlusIcon,
  PhotoIcon,
  LinkIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const API = `${API_BASE}`;

const BannerManagement = ({ onBack }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API}/admin/banners`);
      setBanners(response.data);
    } catch (error) {
      toast.error('広告バナーの取得に失敗しました');
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };



  const deleteBanner = async (bannerId) => {
    if (!window.confirm('このバナーを削除しますか？')) return;

    try {
      await axios.delete(`${API}/admin/banners/${bannerId}`);
      toast.success('バナーを削除しました');
      fetchBanners();
    } catch (error) {
      toast.error('バナーの削除に失敗しました');
      console.error('Error deleting banner:', error);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingBanner(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center mb-6">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-gray-900">
            <PhotoIcon className="h-8 w-8 inline-block mr-3" />
            広告バナー管理
          </h1>
          <div className="flex space-x-3">

            <button
              onClick={handleCreate}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              新規作成
            </button>
          </div>
        </div>
        <p className="text-gray-600">
          トップページに表示される広告バナーを管理します。画像にリンクを設定して、ユーザーを特定のページに誘導できます。
        </p>
      </div>

      {/* バナー一覧 */}
      <div className="bg-white rounded-lg shadow-lg">
        {banners.length === 0 ? (
          <div className="p-12 text-center">
            <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">広告バナーがありません</h3>
            <p className="text-gray-600 mb-6">新しいバナーを作成して、トップページをカスタマイズしましょう。</p>
            <button
              onClick={handleCreate}
              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700"
            >
              最初のバナーを作成
            </button>
          </div>
        ) : (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    バナー
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    詳細
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    依頼者
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    統計
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ステータス
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {banners.map((banner) => (
                  <tr key={banner.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-20 w-32">
                          <img
                            className="h-20 w-32 object-cover rounded-lg border border-gray-200"
                            src={banner.image_url}
                            alt={banner.title}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {banner.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            表示順: {banner.display_order}
                          </div>
                        </div>
                      </div>

                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {banner.employer_name || '不明'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {banner.is_admin_created ? '運営作成' : '有料広告'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {banner.description || 'なし'}
                      </div>
                      {banner.link_url && (
                        <div className="flex items-center text-sm text-blue-600 mt-1">
                          <LinkIcon className="h-4 w-4 mr-1" />
                          <span className="truncate max-w-48">{banner.link_url}</span>
                        </div>
                      )}
                      {(banner.start_date || banner.end_date) && (
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <span>
                            {banner.start_date ? new Date(banner.start_date).toLocaleDateString('ja-JP') : '開始日なし'}
                            {' - '}
                            {banner.end_date ? new Date(banner.end_date).toLocaleDateString('ja-JP') : '終了日なし'}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center text-gray-600">
                          <EyeIcon className="h-4 w-4 mr-1" />
                          表示: {banner.view_count.toLocaleString()}
                        </div>
                        <div className="text-gray-600 mt-1">
                          クリック: {banner.click_count.toLocaleString()}
                        </div>
                        {banner.view_count > 0 && (
                          <div className="text-gray-500 text-xs mt-1">
                            CTR: {((banner.click_count / banner.view_count) * 100).toFixed(2)}%
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${banner.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {banner.is_active ? 'アクティブ' : '非アクティブ'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(banner)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteBanner(banner.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* バナー作成・編集フォーム */}
      {
        showForm && (
          <BannerForm
            banner={editingBanner}
            onClose={() => {
              setShowForm(false);
              setEditingBanner(null);
            }}
            onSave={() => {
              setShowForm(false);
              setEditingBanner(null);
              fetchBanners();
            }}
          />
        )
      }
    </div >
  );
};

const BannerForm = ({ banner, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: banner?.title || '',
    image_url: banner?.image_url || '',
    link_url: banner?.link_url || '',
    description: banner?.description || '',
    is_active: banner?.is_active ?? true,
    display_order: banner?.display_order || 0,
    start_date: banner?.start_date ? banner.start_date.split('T')[0] : '',
    end_date: banner?.end_date ? banner.end_date.split('T')[0] : ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      if (banner) {
        await axios.put(`${API}/admin/banners/${banner.id}`, submitData);
        toast.success('バナーを更新しました');
      } else {
        await axios.post(`${API}/admin/banners`, submitData);
        toast.success('バナーを作成しました');
      }

      onSave();
    } catch (error) {
      toast.error(banner ? 'バナーの更新に失敗しました' : 'バナーの作成に失敗しました');
      console.error('Error saving banner:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result;
        setFormData(prev => ({
          ...prev,
          image_url: base64String
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {banner ? 'バナー編集' : 'バナー作成'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                バナータイトル
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                画像設定
              </label>
              <div className="space-y-3">
                {/* 画像URL入力 */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">画像URL</label>
                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    required
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* ファイルアップロード */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">または画像ファイルをアップロード (JPEG/PNG)</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    onChange={handleImageUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    推奨サイズ: 1200x300px (横長バナー用)
                  </p>
                </div>
              </div>

              {formData.image_url && (
                <div className="mt-2">
                  <p className="text-xs text-gray-600 mb-1">プレビュー (50px高さで表示)</p>
                  <img
                    src={formData.image_url}
                    alt="プレビュー"
                    className="w-full object-cover rounded border border-gray-200"
                    style={{ height: '50px' }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                リンクURL（任意）
              </label>
              <input
                type="url"
                name="link_url"
                value={formData.link_url}
                onChange={handleChange}
                placeholder="https://example.com/"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                説明（任意）
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  表示順
                </label>
                <input
                  type="number"
                  name="display_order"
                  value={formData.display_order}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">アクティブ</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  開始日（任意）
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  終了日（任意）
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? '保存中...' : (banner ? '更新' : '作成')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BannerManagement;